import {useCallback, useEffect, useRef, useState, type MutableRefObject} from 'react'

export type VoiceInputStatus = 'idle' | 'connecting' | 'recording' | 'stopping'

interface UseVoiceInputOptions {
  /** Function that fetches a temporary AssemblyAI streaming token from the backend */
  fetchToken: () => Promise<string>
  onTranscribed: (text: string) => void
  onError?: (message: string) => void
}

interface UseVoiceInputReturn {
  status: VoiceInputStatus
  transcript: string
  /** Live mic level 0–1 for the waveform — a REF, not state. As state it triggered a ~60fps re-render
   *  of the component hosting this hook (R3Chat) for the whole recording, saturating the main thread
   *  in long chats and starving the ScriptProcessor (garbled transcription). The Waveform runs its own
   *  rAF loop and reads this ref each frame instead. */
  audioLevelRef: MutableRefObject<number>
  /** Start recording (no-op if already recording). */
  start: () => void
  /** Stop + KEEP: returns the final transcribed text (the ✓ accept action). */
  accept: () => string
  /** Stop + DISCARD: returns '' and drops the transcript (the ✕ reject action). */
  cancel: () => void
}

// AssemblyAI v3 Streaming Speech-to-Text WebSocket endpoint
const ASSEMBLYAI_STREAMING_URL = 'wss://streaming.assemblyai.com/v3/ws'
const SAMPLE_RATE = 16_000
const SCRIPT_PROCESSOR_BUFFER = 4096

/**
 * Downsample a Float32Array from the source sample rate to the target sample rate
 * using linear interpolation, then convert to Int16 PCM.
 */
function downsampleToInt16(buffer: Float32Array, inputRate: number, outputRate: number): ArrayBuffer {
  if (inputRate === outputRate) {
    const int16 = new Int16Array(buffer.length)
    for (let i = 0; i < buffer.length; i++) {
      const s = Math.max(-1, Math.min(1, buffer[i]))
      int16[i] = s < 0 ? s * 0x8000 : s * 0x7FFF
    }
    return int16.buffer
  }

  const ratio = inputRate / outputRate
  const outputLength = Math.round(buffer.length / ratio)
  const int16 = new Int16Array(outputLength)

  for (let i = 0; i < outputLength; i++) {
    const srcIndex = i * ratio
    const srcIndexFloor = Math.floor(srcIndex)
    const srcIndexCeil = Math.min(srcIndexFloor + 1, buffer.length - 1)
    const fraction = srcIndex - srcIndexFloor

    const sample = buffer[srcIndexFloor] + fraction * (buffer[srcIndexCeil] - buffer[srcIndexFloor])
    const clamped = Math.max(-1, Math.min(1, sample))
    int16[i] = clamped < 0 ? clamped * 0x8000 : clamped * 0x7FFF
  }

  return int16.buffer
}

export function useVoiceInput({fetchToken, onTranscribed, onError}: UseVoiceInputOptions): UseVoiceInputReturn {
  const [status, setStatus] = useState<VoiceInputStatus>('idle')
  const [transcript, setTranscript] = useState('')
  const audioLevelRef = useRef(0)

  const socketRef = useRef<WebSocket | null>(null)
  const streamRef = useRef<MediaStream | null>(null)
  const audioContextRef = useRef<AudioContext | null>(null)
  const processorRef = useRef<ScriptProcessorNode | null>(null)
  const analyserRef = useRef<AnalyserNode | null>(null)
  const rafRef = useRef<number | null>(null)
  const finalTextRef = useRef('')
  // The UI displays partial turns immediately. Keep the exact visible value independently from
  // finalized turns so accepting dictation never discards words that AssemblyAI has not yet marked
  // end_of_turn.
  const latestTextRef = useRef('')
  const statusRef = useRef<VoiceInputStatus>('idle')

  useEffect(() => {
    statusRef.current = status
  }, [status])

  const cleanup = useCallback(() => {
    if (rafRef.current !== null) { cancelAnimationFrame(rafRef.current); rafRef.current = null }
    if (analyserRef.current) { analyserRef.current.disconnect(); analyserRef.current = null }
    audioLevelRef.current = 0
    if (processorRef.current) {
      processorRef.current.disconnect()
      processorRef.current.onaudioprocess = null
      processorRef.current = null
    }
    if (audioContextRef.current) {
      audioContextRef.current.close().catch(() => {})
      audioContextRef.current = null
    }
    if (streamRef.current) {
      streamRef.current.getTracks().forEach(t => t.stop())
      streamRef.current = null
    }
    if (socketRef.current) {
      if (socketRef.current.readyState === WebSocket.OPEN) {
        socketRef.current.send(JSON.stringify({type: 'Terminate'}))
      }
      socketRef.current.close()
      socketRef.current = null
    }
  }, [])

  useEffect(() => {
    return () => {
      cleanup()
    }
  }, [cleanup])

  const start = useCallback(async () => {
    if (statusRef.current !== 'idle') return
    statusRef.current = 'connecting'
    setStatus('connecting')
    setTranscript('')
    finalTextRef.current = ''
    latestTextRef.current = ''

    let token: string
    try {
      token = await fetchToken()
    } catch (err: any) {
      console.error('[useVoiceInput] Token fetch failed:', err)
      statusRef.current = 'idle'
      setStatus('idle')
      onError?.(err.message || 'Failed to get transcription token')
      return
    }

    let stream: MediaStream
    try {
      stream = await navigator.mediaDevices.getUserMedia({
        audio: {
          channelCount: 1,
          echoCancellation: true,
          noiseSuppression: true
        }
      })
      streamRef.current = stream
    } catch (err: any) {
      console.error('[useVoiceInput] Microphone error:', err)
      statusRef.current = 'idle'
      setStatus('idle')
      if (err.name === 'NotAllowedError') {
        onError?.('Microphone permission denied. Please allow microphone access and try again.')
      } else {
        onError?.('Could not access microphone')
      }
      return
    }

    // v3 Streaming API — token passed as query param (browser WebSocket can't set headers)
    const wsUrl = `${ASSEMBLYAI_STREAMING_URL}?token=${encodeURIComponent(token)}&sample_rate=${SAMPLE_RATE}`
    const socket = new WebSocket(wsUrl)
    socket.binaryType = 'arraybuffer'
    socketRef.current = socket

    socket.onopen = () => {
      const audioContext = new AudioContext()
      audioContextRef.current = audioContext
      const source = audioContext.createMediaStreamSource(stream)
      const processor = audioContext.createScriptProcessor(SCRIPT_PROCESSOR_BUFFER, 1, 1)
      processorRef.current = processor

      processor.onaudioprocess = (event) => {
        if (socketRef.current?.readyState !== WebSocket.OPEN) return
        const inputData = event.inputBuffer.getChannelData(0)
        const pcm16 = downsampleToInt16(inputData, audioContext.sampleRate, SAMPLE_RATE)
        socketRef.current.send(pcm16)
      }

      source.connect(processor)
      processor.connect(audioContext.destination)

      // Analyser → live RMS level for the waveform animation.
      const analyser = audioContext.createAnalyser()
      analyser.fftSize = 256
      source.connect(analyser)
      analyserRef.current = analyser
      const data = new Uint8Array(analyser.frequencyBinCount)
      const tick = () => {
        if (!analyserRef.current) return
        analyserRef.current.getByteTimeDomainData(data)
        let sum = 0
        for (let i = 0; i < data.length; i++) { const v = (data[i] - 128) / 128; sum += v * v }
        const rms = Math.sqrt(sum / data.length)
        audioLevelRef.current = Math.min(1, rms * 3) // scale up for visible motion; ref — no re-render
        rafRef.current = requestAnimationFrame(tick)
      }
      rafRef.current = requestAnimationFrame(tick)

      statusRef.current = 'recording'
      setStatus('recording')
    }

    socket.onmessage = (event) => {
      let message: any
      try {
        message = JSON.parse(event.data)
      } catch {
        return
      }

      if (message.error) {
        console.error('[useVoiceInput] AssemblyAI error:', message.error)
        return
      }

      // v3 Streaming API uses Turn events with end_of_turn flag
      if (message.type === 'Turn' && message.transcript) {
        if (message.end_of_turn) {
          // Final turn — append to accumulated text
          finalTextRef.current += (finalTextRef.current ? ' ' : '') + message.transcript
          latestTextRef.current = finalTextRef.current
        } else {
          // Partial turn — show accumulated + current partial
          latestTextRef.current = finalTextRef.current + (finalTextRef.current ? ' ' : '') + message.transcript
        }
        setTranscript(latestTextRef.current)
      }
    }

    socket.onerror = () => {
      console.error('[useVoiceInput] WebSocket error')
      cleanup()
      statusRef.current = 'idle'
      setStatus('idle')
      setTranscript('')
      latestTextRef.current = ''
      onError?.('Voice transcription connection failed')
    }

    socket.onclose = () => {
      if (statusRef.current === 'recording') {
        console.warn('[useVoiceInput] WebSocket closed unexpectedly')
        cleanup()
        const text = latestTextRef.current.trim()
        if (text) {
          onTranscribed(text)
        }
        statusRef.current = 'idle'
        setStatus('idle')
        setTranscript('')
        finalTextRef.current = ''
        latestTextRef.current = ''
      }
    }
  }, [fetchToken, onError, cleanup, onTranscribed])

  // Accept: stop recording and return exactly the transcript currently visible to the user (✓),
  // including the latest partial turn.
  const accept = useCallback((): string => {
    if (statusRef.current !== 'recording') return ''
    statusRef.current = 'stopping'
    setStatus('stopping')
    cleanup()
    const text = latestTextRef.current.trim()
    finalTextRef.current = ''
    latestTextRef.current = ''
    statusRef.current = 'idle'
    setStatus('idle')
    setTranscript('')
    return text
  }, [cleanup])

  // Cancel: stop recording and DISCARD the transcript (✕).
  const cancel = useCallback((): void => {
    if (statusRef.current !== 'recording' && statusRef.current !== 'connecting') return
    statusRef.current = 'stopping'
    setStatus('stopping')
    cleanup()
    finalTextRef.current = ''
    latestTextRef.current = ''
    statusRef.current = 'idle'
    setStatus('idle')
    setTranscript('')
  }, [cleanup])

  const startStable = useCallback(() => { void start() }, [start])

  return {status, transcript, audioLevelRef, start: startStable, accept, cancel}
}
